import React, { useEffect, useMemo, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import {
  Activity,
  CalendarDays,
  Clock3,
  Dumbbell,
  Flame,
  Moon,
  Share,
  Sun,
  Target,
  TimerReset,
  Users2,
  Zap,
} from "lucide-react";
import UserTopNav from "../../../components/layout/UserTopNav";
import SessionShareModal from "../components/SessionShareModal";
import { getDateInputValueInTimeZone } from "../../../utils/date";
import { gymConfig } from "../../../config/gymConfig";
import { db } from "../../../firebase";

type SessionKey = "AM" | "PM" | "930AM";
type TimerMode = "timed" | "stationControlled";
type Phase = "WORK" | "REST" | "FINISHED";

type Movement = {
  id?: string;
  name: string;
  target?: string;
  notes?: string;
};

type Station = {
  id?: string;
  title: string;
  movements: Movement[];
};

export default function WODDisplay() {
  const timeZone = "Europe/London";
  const [wod, setWod] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [sessionKey, setSessionKey] = useState<SessionKey>("AM");
  const [loading, setLoading] = useState<boolean>(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [now, setNow] = useState<Date>(() => new Date());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const selectedDateObj = useMemo(() => {
    if (!selectedDate) return null;
    const d = new Date(selectedDate);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [selectedDate]);

  const dayName = useMemo(() => {
    if (!selectedDateObj) return "";
    return selectedDateObj.toLocaleDateString("en-GB", { weekday: "long" });
  }, [selectedDateObj]);

  const strengthTitle = "Strength";

  const fetchWODForDate = async (dateString: string, key: SessionKey) => {
    setLoading(true);
    try {
      const docRef = doc(db, "wods", dateString);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const sessionData = (data as any)[key];
        setWod(sessionData || null);
      } else {
        setWod(null);
      }
    } catch (error) {
      console.error("Error fetching WOD:", error);
      setWod(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedDate(getDateInputValueInTimeZone(new Date(), timeZone));
  }, []);

  useEffect(() => {
    if (selectedDate) fetchWODForDate(selectedDate, sessionKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, sessionKey]);

  const timerMode: TimerMode = useMemo(() => {
    const mode = wod?.timerMode;
    return mode === "stationControlled" ? "stationControlled" : "timed";
  }, [wod?.timerMode]);

  const groupSize: number | null = useMemo(() => {
    const value = wod?.groupSize;
    return typeof value === "number" && value > 0 ? value : null;
  }, [wod?.groupSize]);

  const stations: Station[] = useMemo(() => {
    return normalizeStations(wod?.stations, wod?.movements);
  }, [wod?.stations, wod?.movements]);

  const stationCount = stations.length;

  const controlStationIndex: number | null = useMemo(() => {
    const value = wod?.controlStationIndex;
    return typeof value === "number" && value >= 0 ? value : null;
  }, [wod?.controlStationIndex]);

  const controlStationTitle: string | null = useMemo(() => {
    if (controlStationIndex == null) return null;
    return stations[controlStationIndex]?.title ?? null;
  }, [controlStationIndex, stations]);

  const roundDurationSeconds: number | null = useMemo(() => {
    const value = wod?.roundDurationSeconds;
    if (typeof value === "number" && value > 0) return value;
    return parseDurationToSeconds(wod?.duration);
  }, [wod?.duration, wod?.roundDurationSeconds]);

  const rounds: number | null = useMemo(() => {
    const value = wod?.rounds;
    if (typeof value === "number" && value >= 1) return value;
    if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) {
      return Math.max(1, Math.floor(Number(value)));
    }
    return null;
  }, [wod?.rounds]);

  const restBetweenRoundsSeconds: number = useMemo(() => {
    const value = wod?.restBetweenRoundsSeconds;
    return typeof value === "number" && value >= 0 ? value : 0;
  }, [wod?.restBetweenRoundsSeconds]);

  const totalSessionSeconds = useMemo(() => {
    if (!roundDurationSeconds || !rounds) return null;
    return roundDurationSeconds * rounds + restBetweenRoundsSeconds * Math.max(0, rounds - 1);
  }, [restBetweenRoundsSeconds, roundDurationSeconds, rounds]);

  const sessionHeaderBits = useMemo(() => {
    if (!wod) return { type: "—", style: "—", extra: "—" };

    if (wod.sessionType === "HYROX") {
      const groupLabel = groupSize ? `Group of ${groupSize}` : "Group session";
      const modeLabel = timerMode === "timed" ? "Timed rotation" : "Station controlled";
      return {
        type: "HYROX",
        style: (wod.wodType ?? "Session").toString(),
        extra: `${groupLabel} • ${modeLabel}`,
      };
    }

    if (wod.sessionType === "Strength") {
      return {
        type: "Strength",
        style: strengthTitle,
        extra: `${wod?.strengthMovements?.length ?? 0} programmed stations`,
      };
    }

    return {
      type: String(wod.sessionType ?? "Session"),
      style: String(wod.wodType ?? "Program"),
      extra: "Programmed session",
    };
  }, [groupSize, strengthTitle, timerMode, wod]);

  const sessionTimeLabel = useMemo(() => getSessionTimeLabel(sessionKey), [sessionKey]);

  const sessionTitle = useMemo(() => {
    if (!wod) return "";
    return wod.wodName?.trim() || `${dayName || "Today"} ${sessionHeaderBits.type}`;
  }, [dayName, sessionHeaderBits.type, wod]);

  const heroStats = useMemo(() => {
    if (!wod) return [];

    if (wod.sessionType === "Strength") {
      return [
        { label: "Stations", value: `${wod?.strengthMovements?.length ?? 0}`, icon: Dumbbell },
        { label: "Goal", value: String(wod?.strengthGoal ?? "Quality reps"), icon: Target },
        { label: "Load", value: String(wod?.strengthLoad ?? "% of 1RM"), icon: Activity },
        { label: "Range", value: String(wod?.strengthRange ?? "Hit target reps"), icon: Zap },
      ];
    }

    return [
      { label: "Format", value: sessionHeaderBits.style, icon: Flame },
      { label: "Stations", value: `${stationCount}`, icon: Dumbbell },
      {
        label: timerMode === "timed" ? "Timer" : "Control",
        value:
          timerMode === "timed"
            ? roundDurationSeconds && rounds
              ? `${formatSeconds(roundDurationSeconds)} x ${rounds}`
              : "Not set"
            : controlStationTitle || "Choose station",
        icon: timerMode === "timed" ? TimerReset : Target,
      },
      {
        label: "Group",
        value: groupSize ? `${groupSize} athletes` : "Open floor",
        icon: Users2,
      },
    ];
  }, [
    controlStationTitle,
    groupSize,
    roundDurationSeconds,
    rounds,
    sessionHeaderBits.style,
    stationCount,
    timerMode,
    wod,
  ]);

  const sidebarMetrics = useMemo(() => {
    if (!wod) return [];

    if (wod.sessionType === "Strength") {
      return [
        { label: "Session type", value: sessionHeaderBits.type },
        { label: "Block", value: sessionHeaderBits.style },
        { label: "Stations", value: `${wod?.strengthMovements?.length ?? 0}` },
      ];
    }

    return [
      { label: "Session type", value: sessionHeaderBits.type },
      { label: "Format", value: sessionHeaderBits.style },
      { label: "Rotation", value: timerMode === "timed" ? "Clock led" : "Station led" },
      { label: "Duration", value: totalSessionSeconds ? formatLongDuration(totalSessionSeconds) : "Not set" },
      { label: "Rounds", value: rounds ? `${rounds}` : "—" },
    ];
  }, [
    rounds,
    sessionHeaderBits.style,
    sessionHeaderBits.type,
    timerMode,
    totalSessionSeconds,
    wod,
  ]);

  const stationPreviewLines = useMemo<string[]>(() => {
    if (wod?.sessionType === "Strength") {
      return (Array.isArray(wod?.strengthMovements) ? wod.strengthMovements : [])
        .map((movement: any, index: number) => {
          const name = String(movement?.movement ?? "").trim() || `Station ${index + 1}`;
          const repRange = String(movement?.repRange ?? "").trim();
          return repRange ? `${name} • ${repRange}` : name;
        })
        .slice(0, 6);
    }

    return stations
      .map((station, index) => {
        const title = station.title?.trim() || `Station ${index + 1}`;
        const firstMovement = station.movements.find((movement) => String(movement?.name ?? "").trim().length > 0);
        return firstMovement ? `${title} • ${firstMovement.name}` : title;
      })
      .slice(0, 6);
  }, [stations, wod]);

  const sharePayload = useMemo(() => {
    if (!wod || !selectedDateObj) return null;

    const dateLabel = selectedDateObj.toLocaleDateString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });

    if (wod.sessionType === "Strength") {
      const movements = Array.isArray(wod.strengthMovements) ? wod.strengthMovements : [];
      const items = movements
        .map((movement: any) => {
          const name = String(movement?.movement ?? "").trim();
          const percent = String(movement?.percent ?? "").trim();
          const repRange = String(movement?.repRange ?? "").trim();
          const details = [percent, repRange].filter(Boolean).join(" • ");
          return name ? `${name}${details ? ` • ${details}` : ""}` : "";
        })
        .filter(Boolean);

      return {
        title: wod.wodName?.trim() || strengthTitle,
        subtitle: dayName ? `${dayName} strength session` : "Programmed strength session",
        filename: `${selectedDate}-${sessionKey.toLowerCase()}-session.png`,
        shareTitle: `${sessionTimeLabel} session`,
        shareText: `Today's ${sessionTimeLabel.toLowerCase()} session is live: ${wod.wodName?.trim() || strengthTitle}`,
        dateLabel,
        sessionLabel: sessionKey,
        sessionTimeLabel,
        sessionType: sessionHeaderBits.type,
        sessionStyle: sessionHeaderBits.style,
        sessionExtra: sessionHeaderBits.extra,
        highlight: `${movements.length || 0}`,
        highlightLabel: "Stations",
        stationsLabel: `${movements.length || 0} strength stations`,
        coachNote: String(wod?.strengthCue ?? "").trim() || undefined,
        items,
      };
    }

    const items = stations
      .map((station: Station, index: number) => {
        const title = (station.title || `Station ${index + 1}`).trim();
        const movementNames = station.movements
          .map((movement) => String(movement.name ?? "").trim())
          .filter(Boolean)
          .slice(0, 2)
          .join(" + ");

        return movementNames ? `${title} • ${movementNames}` : title;
      })
      .filter(Boolean);

    const highlight =
      timerMode === "timed"
        ? roundDurationSeconds && rounds
          ? `${formatSeconds(roundDurationSeconds)} x ${rounds}`
          : `${stationCount || 0}`
        : controlStationIndex != null
        ? `${controlStationIndex + 1}/${stationCount || 1}`
        : `${stationCount || 0}`;

    const highlightLabel =
      timerMode === "timed"
        ? "Timer"
        : controlStationIndex != null
        ? "Control"
        : "Stations";

    const formatLabel =
      timerMode === "timed"
        ? `${stationCount || 0} stations • ${rounds || 1} rounds`
        : controlStationTitle
        ? `Control station: ${controlStationTitle}`
        : `${stationCount || 0} stations`;

    return {
      title: wod.wodName?.trim() || `${dayName || "Daily"} Session`,
      subtitle: dayName ? `${dayName} HYROX session` : "Programmed HYROX session",
      filename: `${selectedDate}-${sessionKey.toLowerCase()}-session.png`,
      shareTitle: `${sessionTimeLabel} session`,
      shareText: `Today's ${sessionTimeLabel.toLowerCase()} session is live: ${wod.wodName?.trim() || "HYROX session"}`,
      dateLabel,
      sessionLabel: sessionKey,
      sessionTimeLabel,
      sessionType: sessionHeaderBits.type,
      sessionStyle: sessionHeaderBits.style,
      sessionExtra: sessionHeaderBits.extra,
      highlight,
      highlightLabel,
      stationsLabel: formatLabel,
      coachNote: undefined,
      items,
    };
  }, [
    controlStationIndex,
    controlStationTitle,
    dayName,
    roundDurationSeconds,
    rounds,
    selectedDate,
    selectedDateObj,
    sessionHeaderBits.extra,
    sessionHeaderBits.style,
    sessionHeaderBits.type,
    sessionKey,
    sessionTimeLabel,
    stationCount,
    stations,
    strengthTitle,
    timerMode,
    wod,
  ]);

  return (
    <div className="app-page min-h-screen overflow-hidden bg-[#04060a] text-white">
      <UserTopNav />

      <div className="relative isolate">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute left-[12%] top-[-10%] h-[18rem] w-[18rem] rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.06),transparent_70%)] blur-3xl" />
          <div className="absolute right-[8%] top-[6%] h-[16rem] w-[16rem] rounded-full bg-[radial-gradient(circle,rgba(79,110,247,0.14),transparent_72%)] blur-3xl" />
        </div>

        <div className="relative mx-auto flex w-full max-w-[1600px] flex-col gap-6 px-4 py-4 sm:px-6 lg:px-8 lg:py-6">
          <DisplayControlBar
            currentTime={now}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            sessionKey={sessionKey}
            onSessionChange={setSessionKey}
            canShare={Boolean(wod)}
            onShare={() => setShareOpen(true)}
          />

          {!selectedDate ? null : loading ? (
            <DisplayNotice
              title="Loading session"
              message="Pulling the latest programming for the selected class block."
            />
          ) : !wod ? (
            <DisplayNotice
              title="No session programmed"
              message="There isn’t a workout loaded for this date and time slot yet."
            />
          ) : (
            <>
              <section className="overflow-hidden rounded-[32px] border border-[rgba(123,144,255,0.14)] bg-[#060708] shadow-[0_28px_90px_rgba(0,0,0,0.45)]">
                <div className="relative border-b border-white/8 px-6 py-8 sm:px-10">
                  <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-[radial-gradient(circle_at_top_left,rgba(79,110,247,0.22),transparent_55%)]" />
                  <div className="pointer-events-none absolute right-0 top-0 h-full w-40 bg-[linear-gradient(135deg,transparent,rgba(79,110,247,0.08))]" />
                  <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.32em] text-white/40">
                    <span>{gymConfig.brand.shortName}</span>
                    <span className="h-1 w-1 rounded-full bg-white/25" />
                    <span>Display</span>
                  </div>

                  <h1 className="relative mt-5 max-w-6xl text-5xl font-semibold tracking-[-0.06em] text-white sm:text-6xl xl:text-[5.8rem] xl:leading-[0.88]">
                    {sessionTitle}
                  </h1>

                  <p className="relative mt-4 max-w-3xl text-base text-white/58 sm:text-lg">
                    {selectedDateObj
                      ? selectedDateObj.toLocaleDateString("en-GB", {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })
                      : "—"}
                    {" • "}
                    {sessionTimeLabel}
                  </p>

                  <div className="relative mt-7">
                    <TVHeader
                      selectedDate={selectedDateObj}
                      sessionKey={sessionKey}
                      type={sessionHeaderBits.type}
                      style={sessionHeaderBits.style}
                      extra={sessionHeaderBits.extra}
                      currentTime={now}
                    />
                  </div>
                </div>

                <div className="px-6 py-8 sm:px-10">
                  <div className="space-y-8">
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      {heroStats.map((stat) => (
                        <HeroStat key={stat.label} label={stat.label} value={stat.value} icon={stat.icon} />
                      ))}
                    </div>

                    {wod.sessionType === "HYROX" ? (
                      timerMode === "timed" ? (
                        roundDurationSeconds && rounds ? (
                          <RoundTimer
                            roundDurationSeconds={roundDurationSeconds}
                            rounds={rounds}
                            restBetweenRoundsSeconds={restBetweenRoundsSeconds}
                            totalSessionSeconds={totalSessionSeconds}
                            compact
                          />
                        ) : (
                          <DisplayHeroPanel
                            eyebrow="Timer pending"
                            title="Session clock not set"
                            description="Add round duration and number of rounds in the editor to enable the live TV timer."
                          />
                        )
                      ) : (
                        <ControlStationHero
                          controlIndex={controlStationIndex}
                          total={stationCount}
                          controlName={controlStationTitle}
                        />
                      )
                    ) : (
                      <StrengthOverview
                        title={strengthTitle}
                        movements={wod.strengthMovements || []}
                        goal={wod?.strengthGoal}
                        load={wod?.strengthLoad}
                        range={wod?.strengthRange}
                        cue={wod?.strengthCue}
                      />
                    )}
                  </div>
                </div>
              </section>

              <section className="relative overflow-hidden rounded-[32px] border border-[rgba(123,144,255,0.16)] bg-[#08101a] px-6 py-8 shadow-[0_24px_80px_rgba(0,0,0,0.4)] sm:px-10">
                <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-[linear-gradient(180deg,rgba(79,110,247,0.12),transparent)]" />
                <div className="flex flex-wrap items-end justify-between gap-4 border-b border-white/8 pb-6">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[rgba(219,230,255,0.56)]">
                      Main Floor
                    </div>
                    <h2 className="mt-3 text-4xl font-semibold tracking-[-0.05em] text-white sm:text-[3.25rem]">
                      {wod.sessionType === "Strength" ? strengthTitle : "Station Flow"}
                    </h2>
                    <p className="mt-3 text-base text-white/54">
                      Clean, fast, readable from the back of the room.
                    </p>
                  </div>

                  <div className="rounded-full border border-[rgba(123,144,255,0.2)] bg-[linear-gradient(135deg,rgba(79,110,247,0.14),rgba(79,110,247,0.06))] px-4 py-2 text-sm text-[rgba(219,230,255,0.86)] shadow-[0_0_24px_rgba(79,110,247,0.12)]">
                    {wod.sessionType === "Strength"
                      ? `${wod?.strengthMovements?.length ?? 0} strength stations`
                      : `${stationCount} active stations`}
                  </div>
                </div>

                <div className="mt-10">
                  {wod.sessionType === "Strength" ? (
                    <div className="grid gap-6 md:grid-cols-2 2xl:grid-cols-3">
                      {(wod.strengthMovements || []).map((sm: any, index: number) => (
                        <StrengthStationCard
                          key={`${sm?.movement ?? "movement"}-${index}`}
                          index={index}
                          movement={sm?.movement}
                          percent={sm?.percent}
                          repRange={sm?.repRange}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="grid gap-6 md:grid-cols-2 2xl:grid-cols-3">
                      {stations.map((station: Station, index: number) => {
                        const isControl =
                          timerMode === "stationControlled" &&
                          controlStationIndex != null &&
                          index === controlStationIndex;

                        return (
                          <HyroxStationCard
                            key={station.id ?? index}
                            index={index}
                            station={station}
                            isControl={isControl}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              </section>

              <section className="rounded-[28px] border border-[rgba(123,144,255,0.16)] bg-[linear-gradient(180deg,rgba(79,110,247,0.07),rgba(255,255,255,0.02))] px-6 py-6 shadow-[0_18px_60px_rgba(0,0,0,0.32)] sm:px-8">
                <div className="grid gap-6 xl:grid-cols-[16rem_minmax(0,1fr)]">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[rgba(219,230,255,0.62)]">
                      At A Glance
                    </div>

                    <div className="mt-4 space-y-3">
                      {sidebarMetrics.map((metric) => (
                        <SidebarMetric key={metric.label} label={metric.label} value={metric.value} />
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-white/8 pt-6 xl:border-l xl:border-t-0 xl:pl-6 xl:pt-0">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[rgba(219,230,255,0.62)]">
                      Up Next
                    </div>
                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                      {stationPreviewLines.length ? (
                        stationPreviewLines.slice(0, 4).map((line, index) => (
                          <div
                            key={`${line}-${index}`}
                            className="rounded-2xl border border-[rgba(123,144,255,0.14)] bg-[rgba(79,110,247,0.06)] px-4 py-4 text-base text-white/76"
                          >
                            <span className="mr-2 text-[rgba(179,194,255,0.5)]">{String(index + 1).padStart(2, "0")}</span>
                            {line}
                          </div>
                        ))
                      ) : (
                        <div className="rounded-2xl border border-white/8 bg-black/20 px-4 py-4 text-base text-white/55">
                          No stations added yet.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            </>
          )}
        </div>
      </div>

      {sharePayload ? (
        <SessionShareModal
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          title={sharePayload.title}
          subtitle={sharePayload.subtitle}
          filename={sharePayload.filename}
          shareTitle={sharePayload.shareTitle}
          shareText={sharePayload.shareText}
          dateLabel={sharePayload.dateLabel}
          sessionLabel={sharePayload.sessionLabel}
          sessionTimeLabel={sharePayload.sessionTimeLabel}
          sessionType={sharePayload.sessionType}
          sessionStyle={sharePayload.sessionStyle}
          sessionExtra={sharePayload.sessionExtra}
          highlight={sharePayload.highlight}
          highlightLabel={sharePayload.highlightLabel}
          stationsLabel={sharePayload.stationsLabel}
          coachNote={sharePayload.coachNote}
          items={sharePayload.items}
        />
      ) : null}
    </div>
  );
}

function DisplayControlBar({
  currentTime,
  selectedDate,
  onDateChange,
  sessionKey,
  onSessionChange,
  canShare,
  onShare,
}: {
  currentTime: Date;
  selectedDate: string;
  onDateChange: (value: string) => void;
  sessionKey: SessionKey;
  onSessionChange: (value: SessionKey) => void;
  canShare: boolean;
  onShare: () => void;
}) {
  return (
    <section className="overflow-hidden rounded-[30px] border border-[rgba(123,144,255,0.16)] bg-[linear-gradient(160deg,rgba(8,10,14,0.98),rgba(5,7,11,0.98))] shadow-[0_24px_80px_rgba(0,0,0,0.42)]">
      <div className="flex flex-col gap-5 px-5 py-5 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-[20px] border border-[rgba(123,144,255,0.18)] bg-[rgba(79,110,247,0.08)] p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <img
              src={gymConfig.assets.logoUrl}
              alt={gymConfig.brand.shortName}
              className="h-full w-full object-contain"
            />
          </div>

          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[rgba(219,230,255,0.58)]">
              {gymConfig.brand.shortName}
            </div>
            <div className="mt-1 text-2xl font-semibold tracking-[-0.04em] text-white">
              Locker Room Display
            </div>
            <div className="mt-1 text-sm text-white/54">
              Premium broadcast minimal for the gym floor.
            </div>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-[auto_auto]">
          <div className="rounded-[22px] border border-[rgba(123,144,255,0.16)] bg-[rgba(79,110,247,0.06)] px-4 py-3">
            <div className="mt-2 flex items-center gap-2 text-lg font-semibold text-white">
              <Clock3 className="h-4 w-4 text-[rgba(179,194,255,0.82)]" />
              {currentTime.toLocaleTimeString("en-GB", {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })}
            </div>
          </div>

          <div className="rounded-[22px] border border-[rgba(123,144,255,0.16)] bg-[rgba(79,110,247,0.06)] px-4 py-3">
            <div className="mt-2 flex items-center gap-2 text-lg font-semibold text-white">
              <span className="inline-flex h-2.5 w-2.5 rounded-full bg-[rgb(123,144,255)] shadow-[0_0_14px_rgba(123,144,255,0.75)]" />
              Live
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 border-t border-white/8 px-5 py-5 sm:px-6 xl:grid-cols-[auto_1fr_auto] xl:items-center">
        <label className="flex items-center gap-3 rounded-[22px] border border-white/10 bg-black/20 px-4 py-3">
          <CalendarDays className="h-5 w-5 text-white/70" />
          <input
            type="date"
            value={selectedDate}
            onChange={(event) => onDateChange(event.target.value)}
            className="bg-transparent text-sm font-medium text-white outline-none [color-scheme:dark]"
          />
        </label>

        <div className="grid gap-2 sm:grid-cols-3">
          <SessionToggleButton
            active={sessionKey === "AM"}
            label="AM"
            sublabel="6AM"
            icon={Sun}
            onClick={() => onSessionChange("AM")}
          />
          <SessionToggleButton
            active={sessionKey === "930AM"}
            label="9:30"
            sublabel="9:30AM"
            icon={Clock3}
            onClick={() => onSessionChange("930AM")}
          />
          <SessionToggleButton
            active={sessionKey === "PM"}
            label="PM"
            sublabel="6PM"
            icon={Moon}
            onClick={() => onSessionChange("PM")}
          />
        </div>

        {canShare ? (
          <button
            type="button"
            onClick={onShare}
            className="inline-flex items-center justify-center gap-2 rounded-[22px] border border-white/10 bg-white px-5 py-3 text-sm font-semibold text-black transition hover:opacity-90"
          >
            <Share className="h-4 w-4" />
            Share Session
          </button>
        ) : (
          <div className="rounded-[22px] border border-dashed border-white/10 bg-black/10 px-4 py-3 text-sm text-white/45">
            Load a session to enable sharing.
          </div>
        )}
      </div>
    </section>
  );
}

function SessionToggleButton({
  active,
  label,
  sublabel,
  icon: Icon,
  onClick,
}: {
  active: boolean;
  label: string;
  sublabel: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
          className={[
            "flex items-center justify-between rounded-[22px] border px-4 py-3 text-left transition",
            active
              ? "border-[rgba(123,144,255,0.34)] bg-[linear-gradient(135deg,rgba(255,255,255,0.98),rgba(213,222,255,0.96))] text-black shadow-[0_16px_40px_rgba(123,144,255,0.18)]"
              : "border-white/10 bg-black/20 text-white/78 hover:border-[rgba(123,144,255,0.2)] hover:bg-[rgba(79,110,247,0.06)]",
          ].join(" ")}
    >
      <div>
        <div className="text-base font-semibold">{label}</div>
        <div className={active ? "text-xs text-black/60" : "text-xs text-white/45"}>{sublabel}</div>
      </div>
      <Icon className={active ? "h-5 w-5 text-black/70" : "h-5 w-5 text-white/65"} />
    </button>
  );
}

function DisplayNotice({ title, message }: { title: string; message: string }) {
  return (
    <div className="rounded-[30px] border border-white/10 bg-[linear-gradient(180deg,rgba(8,11,16,0.96),rgba(6,8,12,0.94))] px-6 py-16 text-center shadow-[0_24px_80px_rgba(0,0,0,0.42)]">
      <div className="text-[11px] font-semibold uppercase tracking-[0.35em] text-white/42">
        Gym Floor Display
      </div>
      <h2 className="mt-4 text-3xl font-semibold tracking-[-0.03em] text-white">{title}</h2>
      <p className="mx-auto mt-3 max-w-2xl text-sm text-white/60 sm:text-base">{message}</p>
    </div>
  );
}

function TVHeader({
  selectedDate,
  sessionKey,
  type,
  style,
  extra,
  currentTime,
}: {
  selectedDate: Date | null;
  sessionKey: SessionKey;
  type: string;
  style: string;
  extra: string;
  currentTime: Date;
}) {
  const pills = [
    selectedDate
      ? selectedDate.toLocaleDateString("en-GB", {
          weekday: "long",
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : "—",
    sessionKey,
    type,
    style,
    extra,
  ];

  return (
    <div className="rounded-[24px] border border-white/8 bg-black/20 p-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          {pills.map((pill) => (
            <span
              key={pill}
              className="rounded-full border border-white/8 bg-white/[0.03] px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/62"
            >
              {pill}
            </span>
          ))}
        </div>

        <div className="flex items-center gap-2 rounded-full border border-[rgba(123,144,255,0.2)] bg-[rgba(79,110,247,0.08)] px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-[rgba(219,230,255,0.86)]">
          <span className="inline-flex h-2 w-2 rounded-full bg-[rgb(123,144,255)] shadow-[0_0_10px_rgba(123,144,255,0.7)]" />
          Live now {currentTime.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>
    </div>
  );
}

function HeroStat({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="rounded-[22px] border border-[rgba(123,144,255,0.12)] bg-[linear-gradient(180deg,rgba(79,110,247,0.06),rgba(255,255,255,0.02))] p-4">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[rgba(219,230,255,0.46)]">{label}</span>
        <Icon className="h-4 w-4 text-[rgba(179,194,255,0.56)]" />
      </div>
      <div className="mt-4 text-2xl font-semibold tracking-[-0.03em] text-white">{value || "—"}</div>
    </div>
  );
}

function SidebarMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[20px] border border-[rgba(123,144,255,0.12)] bg-[rgba(79,110,247,0.05)] px-4 py-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[rgba(219,230,255,0.44)]">{label}</div>
      <div className="mt-2 text-lg font-medium leading-7 text-white/84">{value || "—"}</div>
    </div>
  );
}

function DisplayHeroPanel({
  eyebrow,
  title,
  description,
}: {
  eyebrow: string;
  title: string;
  description: string;
}) {
  return (
    <div className="rounded-[28px] border border-[rgba(123,144,255,0.16)] bg-[linear-gradient(145deg,rgba(15,22,38,0.96),rgba(8,11,18,0.98))] p-6 shadow-[0_24px_80px_rgba(0,0,0,0.36)] sm:p-8">
      <div className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[rgba(219,230,255,0.5)]">{eyebrow}</div>
      <div className="mt-4 text-4xl font-semibold tracking-[-0.04em] text-white">{title}</div>
      <p className="mt-4 max-w-2xl text-sm leading-7 text-white/62 sm:text-base">{description}</p>
    </div>
  );
}

function HyroxStationCard({
  index,
  station,
  isControl,
}: {
  index: number;
  station: Station;
  isControl: boolean;
}) {
  const title = (station.title || `Station ${index + 1}`).trim();
  const movements = (station.movements || []).filter((movement) => String(movement?.name ?? "").trim().length > 0);

  return (
    <article
      className={[
        "group relative overflow-hidden rounded-[28px] border p-6 shadow-[0_18px_60px_rgba(0,0,0,0.28)] transition",
        isControl
          ? "border-[rgba(123,144,255,0.28)] bg-[linear-gradient(160deg,rgba(19,28,48,0.96),rgba(8,11,20,0.98))]"
          : "border-white/10 bg-[linear-gradient(160deg,rgba(16,16,16,0.98),rgba(8,8,8,0.98))]",
      ].join(" ")}
    >
      <div className="pointer-events-none absolute right-4 top-2 text-[5.8rem] font-semibold tracking-[-0.08em] text-white/[0.05]">
        {index + 1}
      </div>
      <div
        className={[
          "pointer-events-none absolute inset-x-0 top-0 h-20",
          isControl
            ? "bg-[linear-gradient(180deg,rgba(79,110,247,0.18),transparent)]"
            : "bg-[linear-gradient(180deg,rgba(79,110,247,0.1),transparent)]",
        ].join(" ")}
      />
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/32">
            Station {index + 1}
          </div>
          <h3 className="mt-3 text-3xl font-semibold tracking-[-0.04em] text-white">{title}</h3>
        </div>

        <div
          className={[
            "rounded-2xl border p-3",
            isControl ? "border-[rgba(123,144,255,0.24)] bg-[rgba(79,110,247,0.12)]" : "border-white/10 bg-white/[0.04]",
          ].join(" ")}
        >
          <Flame className={isControl ? "h-5 w-5 text-[rgb(219,230,255)]" : "h-5 w-5 text-white/70"} />
        </div>
      </div>

      {isControl ? (
        <div className="mt-5 inline-flex rounded-full border border-[rgba(123,144,255,0.2)] bg-[rgba(79,110,247,0.1)] px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[rgba(219,230,255,0.84)]">
          Pace station
        </div>
      ) : null}

      <div className="mt-6 space-y-3">
        {movements.length ? (
          movements.map((movement, movementIndex) => {
            const name = String(movement.name ?? "").trim();
            const target = String(movement.target ?? "").trim();
            const notes = String(movement.notes ?? "").trim();

            return (
              <div
                key={movement.id ?? `${name}-${movementIndex}`}
                className="rounded-[22px] border border-white/8 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.01))] px-4 py-4"
              >
                <div className="text-lg font-semibold leading-7 text-white/94">{name || "—"}</div>
                {target || notes ? (
                  <div className="mt-2 text-base leading-7 text-white/50">
                    {target ? <span>Target: {target}</span> : null}
                    {target && notes ? <span className="mx-2 text-white/25">•</span> : null}
                    {notes ? <span>{notes}</span> : null}
                  </div>
                ) : null}
              </div>
            );
          })
        ) : (
          <div className="rounded-[22px] border border-white/8 bg-black/20 px-4 py-4 text-sm text-white/50">
            No movements added.
          </div>
        )}
      </div>
    </article>
  );
}

function StrengthOverview({
  title,
  movements,
  goal,
  load,
  range,
  cue,
}: {
  title: string;
  movements: any[];
  goal?: string;
  load?: string;
  range?: string;
  cue?: string;
}) {
  const goalText = (goal ?? "Quality reps").trim() || "Quality reps";
  const loadText = (load ?? "% of 1RM").trim() || "% of 1RM";
  const rangeText = (range ?? "Hit target reps").trim() || "Hit target reps";
  const cueText = (cue ?? "").trim();

  return (
    <div className="rounded-[32px] border border-[rgba(123,144,255,0.16)] bg-[linear-gradient(150deg,rgba(16,21,34,0.94),rgba(8,8,8,0.98))] p-8 shadow-[0_24px_80px_rgba(0,0,0,0.34)] sm:p-10">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[rgba(219,230,255,0.44)]">
            {gymConfig.brand.shortName}
          </div>
          <div className="mt-3 text-6xl font-semibold tracking-[-0.06em] text-white sm:text-7xl">{title}</div>
        </div>
        <div className="rounded-2xl border border-[rgba(123,144,255,0.18)] bg-[rgba(79,110,247,0.08)] p-3">
          <Dumbbell className="h-6 w-6 text-[rgba(219,230,255,0.82)]" />
        </div>
      </div>

      <div className="mt-8 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <HeroStat label="Stations" value={`${movements.length || 0}`} icon={Dumbbell} />
        <HeroStat label="Goal" value={goalText} icon={Target} />
        <HeroStat label="Load" value={loadText} icon={Activity} />
        <HeroStat label="Range" value={rangeText} icon={Zap} />
      </div>

      {cueText ? (
        <div className="mt-6 rounded-[24px] border border-white/10 bg-black/20 p-5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-white/40">Coach cue</div>
          <div className="mt-3 whitespace-pre-wrap text-sm leading-7 text-white/70">{cueText}</div>
        </div>
      ) : null}
    </div>
  );
}

function StrengthStationCard({
  index,
  movement,
  percent,
  repRange,
}: {
  index: number;
  movement: string;
  percent?: any;
  repRange?: any;
}) {
  const pct = String(percent ?? "").trim();
  const rr = String(repRange ?? "").trim();

  return (
    <article className="relative overflow-hidden rounded-[28px] border border-[rgba(123,144,255,0.12)] bg-[linear-gradient(160deg,rgba(15,19,28,0.96),rgba(8,8,8,0.98))] p-6 shadow-[0_18px_60px_rgba(0,0,0,0.28)]">
      <div className="pointer-events-none absolute right-4 top-2 text-[5.8rem] font-semibold tracking-[-0.08em] text-white/[0.05]">
        {index + 1}
      </div>
      <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-[linear-gradient(180deg,rgba(79,110,247,0.12),transparent)]" />
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/32">
            Station {index + 1}
          </div>
          <h3 className="mt-3 text-3xl font-semibold tracking-[-0.04em] text-white">{movement || "—"}</h3>
        </div>
        <div className="rounded-2xl border border-[rgba(123,144,255,0.16)] bg-[rgba(79,110,247,0.08)] p-3">
          <Dumbbell className="h-5 w-5 text-[rgba(219,230,255,0.8)]" />
        </div>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <SidebarMetric label="Load" value={pct || "—"} />
        <SidebarMetric label="Reps" value={rr || "—"} />
      </div>
    </article>
  );
}

function ControlStationHero({
  controlIndex,
  total,
  controlName,
}: {
  controlIndex: number | null;
  total: number;
  controlName: string | null;
}) {
  const hasControl = controlIndex != null && total > 0 && Boolean(controlName);

  return (
    <div className="rounded-[32px] border border-[rgba(123,144,255,0.18)] bg-[linear-gradient(150deg,rgba(18,30,54,0.84),rgba(8,10,18,0.98))] p-8 shadow-[0_24px_80px_rgba(0,0,0,0.34)] sm:p-10">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[rgba(219,230,255,0.46)]">
            {gymConfig.brand.shortName}
          </div>
          <div className="mt-3 text-6xl font-semibold tracking-[-0.06em] text-white sm:text-7xl">
            {hasControl ? `${controlIndex! + 1}/${total}` : "—"}
          </div>
        </div>
        <div className="rounded-2xl border border-[rgba(123,144,255,0.18)] bg-[rgba(79,110,247,0.1)] p-3">
          <Target className="h-6 w-6 text-[rgba(219,230,255,0.84)]" />
        </div>
      </div>

      <div className="mt-6 max-w-3xl">
        <div className="text-4xl font-semibold tracking-[-0.05em] text-white sm:text-5xl">
          {hasControl ? controlName : "Choose a control station in the editor"}
        </div>
        <p className="mt-4 text-base leading-8 text-white/58 sm:text-lg">
          This station sets the pace for the room. Coaches can anchor movement transitions here and keep the floor synchronized without relying on the clock.
        </p>
      </div>
    </div>
  );
}

function RoundTimer({
  roundDurationSeconds,
  rounds,
  restBetweenRoundsSeconds,
  totalSessionSeconds,
  compact = false,
}: {
  roundDurationSeconds: number;
  rounds: number;
  restBetweenRoundsSeconds: number;
  totalSessionSeconds: number | null;
  compact?: boolean;
}) {
  const safeRoundSeconds = Math.max(0, Math.floor(roundDurationSeconds || 0));
  const safeRounds = Math.max(1, Math.floor(rounds || 1));
  const safeRest = Math.max(0, Math.floor(restBetweenRoundsSeconds || 0));

  const [phase, setPhase] = useState<Phase>("WORK");
  const [roundIndex, setRoundIndex] = useState<number>(1);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [elapsedInPhase, setElapsedInPhase] = useState<number>(0);

  useEffect(() => {
    if (!isRunning) return;

    const timer = window.setInterval(() => {
      setElapsedInPhase((value) => value + 1);
    }, 1000);

    return () => window.clearInterval(timer);
  }, [isRunning]);

  const phaseDuration = phase === "WORK" ? safeRoundSeconds : phase === "REST" ? safeRest : 0;
  const remaining = phase === "FINISHED" ? 0 : Math.max(0, Math.floor(phaseDuration - elapsedInPhase));
  const progress = phaseDuration > 0 ? Math.min(1, Math.max(0, elapsedInPhase / phaseDuration)) : 0;

  useEffect(() => {
    if (!isRunning || phase === "FINISHED" || phaseDuration <= 0) return;
    if (elapsedInPhase >= phaseDuration) advanceToNext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elapsedInPhase, isRunning, phase, phaseDuration]);

  const resetToStart = () => {
    setIsRunning(false);
    setPhase("WORK");
    setRoundIndex(1);
    setElapsedInPhase(0);
  };

  const startOrResume = () => {
    if (phase === "FINISHED") {
      resetToStart();
      setIsRunning(true);
      return;
    }
    setIsRunning(true);
  };

  const pause = () => setIsRunning(false);

  const advanceToNext = () => {
    if (phase === "FINISHED") return;

    if (phase === "WORK") {
      const hasMoreRounds = roundIndex < safeRounds;

      if (!hasMoreRounds) {
        setPhase("FINISHED");
        setIsRunning(false);
        setElapsedInPhase(0);
        return;
      }

      if (safeRest > 0) {
        setPhase("REST");
        setElapsedInPhase(0);
        return;
      }

      setRoundIndex((value) => value + 1);
      setPhase("WORK");
      setElapsedInPhase(0);
      return;
    }

    if (phase === "REST") {
      setRoundIndex((value) => value + 1);
      setPhase("WORK");
      setElapsedInPhase(0);
    }
  };

  const phaseLabel = phase === "WORK" ? "Work" : phase === "REST" ? "Rest" : "Finished";
  const labelTone =
    phase === "WORK"
      ? "text-[rgba(219,230,255,0.96)]"
      : phase === "REST"
      ? "text-[rgba(179,194,255,0.82)]"
      : "text-white/56";

  return (
    <div
      className={[
        "border border-[rgba(123,144,255,0.18)] bg-[linear-gradient(145deg,rgba(12,17,28,0.96),rgba(7,8,12,0.98))] shadow-[0_24px_80px_rgba(0,0,0,0.34)]",
        compact ? "rounded-[24px] p-4 sm:p-5" : "rounded-[30px] p-6 sm:p-7",
      ].join(" ")}
    >
      {compact ? (
        <div className="grid gap-4 xl:grid-cols-[auto_minmax(0,1fr)_auto] xl:items-center">
          <div className="flex items-center gap-4">
            <div className="relative flex h-[132px] w-[132px] items-center justify-center">
              <Ring progress={progress} size={132} stroke={10} />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                <div className="text-[1.95rem] font-semibold tracking-[-0.08em] text-white">
                  {formatSeconds(remaining)}
                </div>
                <div className={`mt-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${labelTone}`}>
                  {phaseLabel}
                </div>
              </div>
            </div>

            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[rgba(219,230,255,0.42)]">
                {gymConfig.brand.shortName}
              </div>
              <div className="mt-2 text-2xl font-semibold tracking-[-0.05em] text-white">
                Room Clock
              </div>
              <div className="mt-2 text-sm text-white/60">
                Round {Math.min(roundIndex, safeRounds)} of {safeRounds}
                {" • "}
                {totalSessionSeconds ? formatLongDuration(totalSessionSeconds) : "Session not set"}
              </div>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <SidebarMetric label="Work" value={formatSeconds(safeRoundSeconds)} />
            <SidebarMetric label="Rest" value={safeRest > 0 ? formatSeconds(safeRest) : "None"} />
            <SidebarMetric label="Status" value={phase === "FINISHED" ? "Complete" : isRunning ? "Running" : "Waiting"} />
          </div>

          <div className="flex flex-wrap items-center gap-3 xl:justify-end">
            {!isRunning ? (
              <button
                type="button"
                onClick={startOrResume}
                className="rounded-full border border-[rgba(123,144,255,0.24)] bg-[linear-gradient(135deg,#f8fbff,#dbe6ff)] px-5 py-3 text-sm font-semibold text-black transition hover:opacity-90"
              >
                {elapsedInPhase === 0 && phase === "WORK" && roundIndex === 1 ? "Start" : "Resume"}
              </button>
            ) : (
              <button
                type="button"
                onClick={pause}
                className="rounded-full border border-[rgba(123,144,255,0.18)] bg-[rgba(79,110,247,0.08)] px-5 py-3 text-sm font-semibold text-[rgba(219,230,255,0.92)] transition hover:bg-[rgba(79,110,247,0.14)]"
              >
                Pause
              </button>
            )}

            <button
              type="button"
              onClick={advanceToNext}
              disabled={phase === "FINISHED"}
              className={[
                "rounded-full border px-5 py-3 text-sm font-semibold transition",
                phase === "FINISHED"
                  ? "cursor-not-allowed border-white/8 bg-white/[0.02] text-white/28"
                  : "border-[rgba(123,144,255,0.18)] bg-[rgba(79,110,247,0.08)] text-[rgba(219,230,255,0.92)] hover:bg-[rgba(79,110,247,0.14)]",
              ].join(" ")}
            >
              Next
            </button>

            <button
              type="button"
              onClick={resetToStart}
              className="rounded-full border border-white/10 bg-black/20 px-5 py-3 text-sm font-semibold text-white/78 transition hover:border-[rgba(123,144,255,0.18)] hover:bg-[rgba(79,110,247,0.06)] hover:text-white"
            >
              Restart
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[rgba(219,230,255,0.42)]">
                {gymConfig.brand.shortName}
              </div>
              <div className="mt-2 text-3xl font-semibold tracking-[-0.05em] text-white sm:text-[2.75rem]">
                Room Clock
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <HeroStat label="Rounds" value={`${safeRounds}`} icon={TimerReset} />
              <HeroStat label="Session" value={totalSessionSeconds ? formatLongDuration(totalSessionSeconds) : "Not set"} icon={Clock3} />
            </div>
          </div>

          <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)] lg:items-center">
            <div className="flex flex-col items-center justify-center gap-6">
              <div className="relative flex items-center justify-center">
                <Ring progress={progress} size={260} stroke={12} />

                <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                  <div className="text-[4rem] font-semibold tracking-[-0.08em] text-white sm:text-[4.75rem]">
                    {formatSeconds(remaining)}
                  </div>
                  <div className={`mt-1 text-sm font-semibold uppercase tracking-[0.22em] ${labelTone}`}>
                    {phaseLabel}
                    {phase !== "FINISHED" && !isRunning ? " paused" : ""}
                  </div>
                  <div className="mt-3 text-base text-white/62">
                    {Math.min(roundIndex, safeRounds)} / {safeRounds}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-3">
                {!isRunning ? (
                  <button
                    type="button"
                    onClick={startOrResume}
                    className="rounded-full border border-[rgba(123,144,255,0.24)] bg-[linear-gradient(135deg,#f8fbff,#dbe6ff)] px-6 py-3 text-sm font-semibold text-black transition hover:opacity-90"
                  >
                    {elapsedInPhase === 0 && phase === "WORK" && roundIndex === 1 ? "Start" : "Resume"}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={pause}
                    className="rounded-full border border-[rgba(123,144,255,0.18)] bg-[rgba(79,110,247,0.08)] px-6 py-3 text-sm font-semibold text-[rgba(219,230,255,0.92)] transition hover:bg-[rgba(79,110,247,0.14)]"
                  >
                    Pause
                  </button>
                )}

                <button
                  type="button"
                  onClick={advanceToNext}
                  disabled={phase === "FINISHED"}
                  className={[
                    "rounded-full border px-6 py-3 text-sm font-semibold transition",
                    phase === "FINISHED"
                      ? "cursor-not-allowed border-white/8 bg-white/[0.02] text-white/28"
                      : "border-[rgba(123,144,255,0.18)] bg-[rgba(79,110,247,0.08)] text-[rgba(219,230,255,0.92)] hover:bg-[rgba(79,110,247,0.14)]",
                  ].join(" ")}
                >
                  Next
                </button>

                <button
                  type="button"
                  onClick={resetToStart}
                  className="rounded-full border border-white/10 bg-black/20 px-6 py-3 text-sm font-semibold text-white/78 transition hover:border-[rgba(123,144,255,0.18)] hover:bg-[rgba(79,110,247,0.06)] hover:text-white"
                >
                  Restart
                </button>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
              <SidebarMetric label="Work" value={formatSeconds(safeRoundSeconds)} />
              <SidebarMetric label="Rest" value={safeRest > 0 ? formatSeconds(safeRest) : "None"} />
              <SidebarMetric label="Status" value={phase === "FINISHED" ? "Complete" : isRunning ? "Running" : "Waiting"} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Ring({
  progress,
  size,
  stroke,
}: {
  progress: number;
  size: number;
  stroke: number;
}) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clampedProgress = Math.min(1, Math.max(0, progress));
  const dashOffset = circumference * (1 - clampedProgress);

  return (
    <svg
      width={size}
      height={size}
      className="pointer-events-none drop-shadow-[0_0_28px_rgba(123,144,255,0.22)]"
    >
      <defs>
        <linearGradient id="display-ring-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="rgba(123,144,255,1)" />
          <stop offset="100%" stopColor="rgba(232,239,255,0.95)" />
        </linearGradient>
      </defs>

      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="rgba(255,255,255,0.08)"
        strokeWidth={stroke}
        fill="transparent"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="url(#display-ring-gradient)"
        strokeWidth={stroke}
        strokeLinecap="round"
        fill="transparent"
        strokeDasharray={circumference}
        strokeDashoffset={dashOffset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}

function formatSeconds(total: number) {
  const safeTotal = Math.max(0, Math.floor(total));
  const minutes = Math.floor(safeTotal / 60);
  const seconds = safeTotal % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function formatLongDuration(total: number) {
  const safeTotal = Math.max(0, Math.floor(total));
  const minutes = Math.floor(safeTotal / 60);
  const seconds = safeTotal % 60;
  return seconds === 0 ? `${minutes} min` : `${minutes} min ${seconds}s`;
}

function parseDurationToSeconds(duration: any): number | null {
  if (!duration || typeof duration !== "string") return null;
  const value = duration.trim();
  if (!value) return null;

  const parts = value.split(":").map((part) => part.trim());
  if (parts.length === 2) {
    const minutes = Number(parts[0]);
    const seconds = Number(parts[1]);
    if (Number.isFinite(minutes) && Number.isFinite(seconds)) return minutes * 60 + seconds;
  }

  const minutesMatch = value.match(/(\d+)\s*m/i);
  const secondsMatch = value.match(/(\d+)\s*s/i);
  const minutes = minutesMatch ? Number(minutesMatch[1]) : 0;
  const seconds = secondsMatch ? Number(secondsMatch[1]) : 0;
  const total = minutes * 60 + seconds;
  return total > 0 ? total : null;
}

function normalizeMovements(raw: any): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((movement) => {
      if (typeof movement === "string") return movement;
      if (movement && typeof movement === "object") {
        const value = movement.partner1 ?? movement.movement ?? "";
        return String(value);
      }
      return String(movement ?? "");
    })
    .map((value) => value.trim())
    .filter(Boolean);
}

function normalizeStations(rawStations: any, rawMovements: any): Station[] {
  if (Array.isArray(rawStations) && rawStations.length) {
    const stations: Station[] = rawStations.map((station: any, index: number) => {
      const title = String(station?.title ?? `Station ${index + 1}`).trim();
      const rawStationMovements = Array.isArray(station?.movements) ? station.movements : [];

      const movements: Movement[] = rawStationMovements
        .map((movement: any) => ({
          id: movement?.id,
          name: String(movement?.name ?? "").trim(),
          target: String(movement?.target ?? "").trim() || undefined,
          notes: String(movement?.notes ?? "").trim() || undefined,
        }))
        .filter((movement: Movement) => movement.name.length > 0 || movement.target || movement.notes);

      return {
        id: station?.id,
        title: title || `Station ${index + 1}`,
        movements,
      };
    });

    return stations.length ? stations : [{ title: "Station 1", movements: [] }];
  }

  const legacyMovements = normalizeMovements(rawMovements);
  if (legacyMovements.length) {
    return [
      {
        title: "Station 1",
        movements: legacyMovements.map((name) => ({ name })),
      },
    ];
  }

  return [{ title: "Station 1", movements: [] }];
}

function getSessionTimeLabel(sessionKey: SessionKey) {
  if (sessionKey === "AM") return "6AM";
  if (sessionKey === "PM") return "6PM";
  return "9:30AM";
}
